import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  type ScriptableContext,
  type TooltipItem,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  ApiError,
  collectLlmUsage,
  getLlmUsage,
  type LlmUsage,
  type UsageCollection,
  type UsageTotals,
} from "@/lib/api";
import { AGENT_SLOTS, OTHER, agentSeries, agentToken, dayLabel, fmtTokens, pct } from "@/lib/usage";
import { timeAgo } from "@/lib/deployments";
import { STATUS_HUE } from "@/lib/statusColors";

ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

const RANGES = [7, 30, 90] as const;
const exact = (n: number) => n.toLocaleString("en-GB");

/**
 * Chart.js takes paint values, not CSS vars: resolve the tokens and again on
 * a theme flip, as the finance Insights charts do.
 */
const CHART_TOKENS = {
  ink: "--color-text-muted",
  grid: "--chart-grid",
  tooltipBg: "--color-bg-elevated",
  tooltipInk: "--color-text-heading",
} as const;

/** Every agent colour, keyed by its token: the slots, then Other. */
const SERIES_TOKENS = [...AGENT_SLOTS.map((_, i) => `--chart-s${i + 1}`), "--chart-other"];

type ChartColors = Record<keyof typeof CHART_TOKENS, string> & { series: Record<string, string> };

function readChartColors(): ChartColors {
  const style = getComputedStyle(document.body);
  const read = (v: string) => style.getPropertyValue(v).trim();
  return {
    ...(Object.fromEntries(Object.entries(CHART_TOKENS).map(([k, v]) => [k, read(v)])) as Record<
      keyof typeof CHART_TOKENS,
      string
    >),
    series: Object.fromEntries(SERIES_TOKENS.map((t) => [t, read(t)])),
  };
}

function useChartColors(): ChartColors {
  const [colors, setColors] = useState(readChartColors);
  useEffect(() => {
    const obs = new MutationObserver(() => setColors(readChartColors()));
    obs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return colors;
}

/**
 * A service's Platform usage tab: the LLM tokens it spends, per agent, model
 * and day, counted from its Render logs every ten minutes (see
 * backend/src/deployments/usage.service.ts).
 */
export function PlatformUsage({ serviceId }: { serviceId: string }) {
  const [days, setDays] = useState<number>(30);
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["usage", serviceId, days],
    queryFn: () => getLlmUsage(serviceId, days),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const collect = useMutation({
    mutationFn: collectLlmUsage,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["usage", serviceId] }),
  });

  const chip = (active: boolean) =>
    `rounded-token-md border px-3 py-1.5 text-sm transition-colors ${
      active
        ? "border-accent bg-accent-muted font-medium text-accent"
        : "border-border text-text-secondary hover:text-text-heading"
    }`;

  if (isLoading) {
    return (
      <div className="py-16 text-center text-text-muted">
        <i className="fa-solid fa-circle-notch fa-spin text-xl" />
      </div>
    );
  }
  if (isError || !data?.configured || !data.totals) {
    return <p className="text-sm text-text-secondary">Could not load usage from Render.</p>;
  }

  return (
    <div>
      {/* Filters: one row, above everything they change. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {RANGES.map((d) => (
          <button key={d} onClick={() => setDays(d)} className={chip(days === d)}>
            {d} days
          </button>
        ))}
        <button
          onClick={() => collect.mutate()}
          disabled={collect.isPending}
          title="Read new log lines now instead of waiting for the next ten-minute run"
          className="ml-auto rounded-token-md border border-border px-3 py-1.5 text-sm text-text-secondary transition-colors hover:border-accent hover:text-text-heading disabled:opacity-50"
        >
          <i className={`fa-solid ${collect.isPending ? "fa-circle-notch fa-spin" : "fa-rotate-right"} mr-1.5 text-xs`} />
          Collect now
        </button>
      </div>
      {collect.isError && (
        <p className="mb-4 rounded-token-md bg-danger/10 px-3 py-2 text-sm text-danger">
          <i className="fa-solid fa-triangle-exclamation mr-1.5" />
          {collect.error instanceof ApiError ? collect.error.message : "Collecting failed"}
        </p>
      )}

      {data.totals.calls + data.totals.failed === 0 ? (
        <div className="mb-4 rounded-token-lg border border-dashed border-border px-6 py-12 text-center">
          <p className="text-sm text-text-secondary">No LLM calls counted in the last {data.days} days.</p>
          {!data.collection && (
            <p className="mt-1 text-xs text-text-muted">
              Its logs haven't been read yet: that happens every ten minutes, or use Collect now.
            </p>
          )}
        </div>
      ) : (
        <>
          <Tiles totals={data.totals} />
          <DailyChart byDay={data.byDay ?? []} />
          <AgentTable rows={data.byAgent ?? []} totals={data.totals} />
          <ModelTable rows={data.byModel ?? []} />
          <AgentModelTable
            agents={data.byAgent ?? []}
            models={data.byModel ?? []}
            cells={data.byAgentModel ?? {}}
            total={data.totals.totalTokens}
          />
        </>
      )}
      <Coverage collection={data.collection ?? null} />
    </div>
  );
}

function Tiles({ totals: t }: { totals: UsageTotals }) {
  const tiles = [
    {
      label: "Tokens",
      value: fmtTokens(t.totalTokens),
      exact: exact(t.totalTokens),
      sub: `${fmtTokens(t.inputTokens)} in · ${fmtTokens(t.outputTokens)} out`,
    },
    {
      label: "Calls",
      value: fmtTokens(t.calls),
      exact: exact(t.calls),
      sub: t.calls ? `${fmtTokens(Math.round(t.totalTokens / t.calls))} tokens per call` : "",
    },
    {
      label: "Input from cache",
      value: pct(t.cachedTokens, t.inputTokens),
      exact: `${exact(t.cachedTokens)} of ${exact(t.inputTokens)} input tokens`,
      sub: `${fmtTokens(t.cachedTokens)} tokens, billed at a tenth`,
    },
    {
      label: "Failed attempts",
      value: fmtTokens(t.failed),
      exact: exact(t.failed),
      sub: `${pct(t.failed, t.calls + t.failed)} of attempts, retries included`,
    },
  ];
  return (
    <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {tiles.map((tile) => (
        <div key={tile.label} className="rounded-token-lg border border-border bg-bg-card p-4 shadow-token-sm">
          <p className="text-xs text-text-muted">{tile.label}</p>
          <p className="mt-1 text-2xl font-semibold text-text-heading" title={tile.exact}>
            {tile.value}
          </p>
          <p className="mt-1 text-xs text-text-secondary">{tile.sub}</p>
        </div>
      ))}
    </div>
  );
}

function DailyChart({ byDay }: { byDay: NonNullable<LlmUsage["byDay"]> }) {
  const c = useChartColors();
  const series = useMemo(() => agentSeries(byDay), [byDay]);
  // Only the top segment of each day's stack gets the rounded end.
  const top = useMemo(
    () => byDay.map((_, day) => series.reduce((t, s, i) => (s.data[day] > 0 ? i : t), -1)),
    [byDay, series],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      // Just the segment under the pointer: one agent on one day.
      interaction: { mode: "point" as const, intersect: true },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: c.tooltipBg,
          titleColor: c.tooltipInk,
          bodyColor: c.tooltipInk,
          borderColor: c.grid,
          borderWidth: 1,
          padding: 10,
          cornerRadius: 6,
          boxPadding: 4,
          callbacks: {
            title: (items: TooltipItem<"bar">[]) => (items[0] ? dayLabel(byDay[items[0].dataIndex].day) : ""),
            label: (item: TooltipItem<"bar">) => `${item.dataset.label}: ${exact(Number(item.raw))} tokens`,
            afterLabel: (item: TooltipItem<"bar">) => {
              const day = byDay[item.dataIndex];
              const share = `${pct(Number(item.raw), day.totalTokens)} of the day's ${fmtTokens(day.totalTokens)}`;
              const s = series[item.datasetIndex];
              if (s.name !== OTHER) return share;
              // Other is several agents: name the ones in this segment.
              const parts = s.members
                .filter((a) => (day.byAgent[a] ?? 0) > 0)
                .sort((a, b) => day.byAgent[b] - day.byAgent[a])
                .map((a) => `${a}: ${fmtTokens(day.byAgent[a])}`);
              return [share, ...parts];
            },
            labelColor: (item: TooltipItem<"bar">) => {
              const color = item.dataset.backgroundColor as string;
              return { borderColor: color, backgroundColor: color };
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { color: c.ink, font: { size: 11 }, maxRotation: 0, autoSkipPadding: 12 },
        },
        y: {
          stacked: true,
          grid: { color: c.grid },
          border: { display: false },
          ticks: { color: c.ink, font: { size: 11 }, callback: (v: number | string) => fmtTokens(Number(v)) },
        },
      },
    }),
    [c, byDay, series],
  );

  return (
    <section className="mb-4 rounded-token-lg border border-border bg-bg-card p-5 shadow-token-sm">
      <h3 className="font-display text-base font-semibold text-text-heading">Tokens per day</h3>
      <p className="mt-0.5 text-xs text-text-muted">By agent, in UTC days. Hover a colour for its figures.</p>
      {/* Legend: always shown, in stack order (bottom up). */}
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-secondary">
        {series.map((s) => (
          <li
            key={s.name}
            className="flex items-center gap-1.5"
            title={s.name === OTHER ? s.members.join(", ") : undefined}
          >
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: `var(${s.token})` }} />
            {s.name === OTHER ? `Other (${s.members.join(", ")})` : s.name}
          </li>
        ))}
      </ul>
      <div className="mt-3 h-72">
        <Bar
          data={{
            labels: byDay.map((d) => dayLabel(d.day).slice(4)),
            datasets: series.map((s, i) => ({
              label: s.name,
              data: s.data,
              backgroundColor: c.series[s.token],
              borderWidth: 0,
              borderRadius: (ctx: ScriptableContext<"bar">) =>
                top[ctx.dataIndex] === i ? { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 } : 0,
              maxBarThickness: 28,
            })),
          }}
          options={options}
        />
      </div>
    </section>
  );
}

const th = "px-3 py-2 text-right text-xs font-medium text-text-muted";
const td = "px-3 py-2 text-right tabular-nums text-text-secondary";

/** An agent's name with its chart colour, so a row reads as the chart's key. */
function AgentName({ agent }: { agent: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: `var(${agentToken(agent)})` }} />
      {agent}
    </span>
  );
}

function AgentTable({ rows, totals }: { rows: NonNullable<LlmUsage["byAgent"]>; totals: UsageTotals }) {
  return (
    <section className="mb-4 overflow-x-auto rounded-token-lg border border-border bg-bg-card shadow-token-sm">
      <table className="w-full text-sm">
        <caption className="px-4 pt-4 pb-2 text-left">
          <span className="font-display text-base font-semibold text-text-heading">By agent</span>
          <span className="ml-2 text-xs text-text-muted">From the task each call is tagged with.</span>
        </caption>
        <thead className="border-b border-border">
          <tr>
            <th className={`${th} pl-4 text-left`}>Agent</th>
            <th className={th}>Tokens</th>
            <th className={`${th} w-48 text-left`}>Share</th>
            <th className={th}>Calls</th>
            <th className={th}>Input</th>
            <th className={th}>Output</th>
            <th className={th}>Cached</th>
            <th className={`${th} pr-4`}>Failed</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-light">
          {rows.map((r) => (
            <tr key={r.agent}>
              <td className="px-3 py-2 pl-4 font-medium text-text-heading">
                <AgentName agent={r.agent} />
              </td>
              <td className={`${td} font-medium text-text-heading`} title={exact(r.totalTokens)}>
                {fmtTokens(r.totalTokens)}
              </td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-bg-secondary">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${totals.totalTokens ? (r.totalTokens / totals.totalTokens) * 100 : 0}%`,
                        background: `var(${agentToken(r.agent)})`,
                      }}
                    />
                  </div>
                  <span className="w-9 text-right text-xs tabular-nums text-text-muted">
                    {pct(r.totalTokens, totals.totalTokens)}
                  </span>
                </div>
              </td>
              <td className={td}>{exact(r.calls)}</td>
              <td className={td} title={exact(r.inputTokens)}>{fmtTokens(r.inputTokens)}</td>
              <td className={td} title={exact(r.outputTokens)}>{fmtTokens(r.outputTokens)}</td>
              <td className={td} title={`${exact(r.cachedTokens)} input tokens from cache`}>
                {pct(r.cachedTokens, r.inputTokens)}
              </td>
              <td className={`${td} pr-4`}>{r.failed ? exact(r.failed) : "–"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ModelTable({ rows }: { rows: NonNullable<LlmUsage["byModel"]> }) {
  return (
    <section className="mb-4 overflow-x-auto rounded-token-lg border border-border bg-bg-card shadow-token-sm">
      <table className="w-full text-sm">
        <caption className="px-4 pt-4 pb-2 text-left font-display text-base font-semibold text-text-heading">
          By model
        </caption>
        <thead className="border-b border-border">
          <tr>
            <th className={`${th} pl-4 text-left`}>Model</th>
            <th className={th}>Tokens</th>
            <th className={th}>Calls</th>
            <th className={th}>Input</th>
            <th className={th}>Output</th>
            <th className={`${th} pr-4`}>Cached</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-light">
          {rows.map((r) => (
            <tr key={r.model}>
              <td className="px-3 py-2 pl-4 font-mono text-xs text-text-heading">{r.model}</td>
              <td className={`${td} font-medium text-text-heading`} title={exact(r.totalTokens)}>
                {fmtTokens(r.totalTokens)}
              </td>
              <td className={td}>{exact(r.calls)}</td>
              <td className={td} title={exact(r.inputTokens)}>{fmtTokens(r.inputTokens)}</td>
              <td className={td} title={exact(r.outputTokens)}>{fmtTokens(r.outputTokens)}</td>
              <td className={`${td} pr-4`}>{pct(r.cachedTokens, r.inputTokens)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** A token count, compact, exact on hover; "–" for none. Totals in heading ink. */
function Cell({ n, strong = false }: { n: number; strong?: boolean }) {
  return (
    <td
      className={`${td} ${strong ? "font-medium text-text-heading" : ""}`}
      title={n ? `${exact(n)} tokens` : undefined}
    >
      {n ? fmtTokens(n) : "–"}
    </td>
  );
}

/**
 * Tokens per agent (rows) per model (columns), each row totalled at its end
 * and each column along the bottom. Same order as the tables above.
 */
function AgentModelTable({
  agents,
  models,
  cells,
  total,
}: {
  agents: NonNullable<LlmUsage["byAgent"]>;
  models: NonNullable<LlmUsage["byModel"]>;
  cells: Record<string, Record<string, number>>;
  total: number;
}) {
  return (
    <section className="mb-4 overflow-x-auto rounded-token-lg border border-border bg-bg-card shadow-token-sm">
      <table className="w-full text-sm">
        <caption className="px-4 pt-4 pb-2 text-left">
          <span className="font-display text-base font-semibold text-text-heading">Agent by model</span>
          <span className="ml-2 text-xs text-text-muted">Tokens each agent spent on each model.</span>
        </caption>
        <thead className="border-b border-border">
          <tr>
            <th className={`${th} pl-4 text-left`}>Agent</th>
            {models.map((m) => (
              <th key={m.model} className={`${th} font-mono`}>
                {m.model}
              </th>
            ))}
            <th className={`${th} pr-4 text-text-heading`}>Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-light">
          {agents.map((a) => (
            <tr key={a.agent}>
              <td className="px-3 py-2 pl-4 font-medium text-text-heading">
                <AgentName agent={a.agent} />
              </td>
              {models.map((m) => (
                <Cell key={m.model} n={cells[a.agent]?.[m.model] ?? 0} />
              ))}
              <Cell n={a.totalTokens} strong />
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t border-border">
          <tr>
            <th scope="row" className={`${th} pl-4 text-left text-text-heading`}>
              Total
            </th>
            {models.map((m) => (
              <Cell key={m.model} n={m.totalTokens} strong />
            ))}
            <Cell n={total} strong />
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

/** How far the logs have been read: what the numbers above can't show. */
function Coverage({ collection: c }: { collection: UsageCollection | null }) {
  const since = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return (
    <section className="text-xs text-text-muted">
      {c ? (
        <p>
          Counting since {since(c.collectedFrom)} · read up to {timeAgo(c.collectedTo)} · last run read{" "}
          {exact(c.lastRunLines)} log lines, {exact(c.lastRunEvents)} of them LLM calls
          {c.lastError && (
            <span className="ml-2" style={{ color: STATUS_HUE.red }}>
              <i className="fa-solid fa-triangle-exclamation mr-1" />
              Last run failed: {c.lastError}
            </span>
          )}
        </p>
      ) : (
        <p>Not read yet.</p>
      )}
      <p className="mt-1">
        Logs are read every ten minutes, starting a week back: Render keeps them no longer, so
        anything earlier was never counted.
      </p>
    </section>
  );
}
